
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Conta } from "@/types/database";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export function useContas() {
  const [contas, setContas] = useState<Conta[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchContas = useCallback(async () => {
    try {
      if (!user?.id) {
        setContas([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await supabase
        .from("contas")
        .select("*")
        .eq("user_id", user.id)
        .neq("tipo", "cartao_credito")
        .order("nome");

      if (error) {
        throw error;
      }

      setContas(data as Conta[]);
    } catch (error) {
      console.error("Erro ao carregar contas:", error);
      toast.error("Erro ao carregar contas");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchContas();
  }, [fetchContas]);

  const createConta = async (conta: Partial<Conta>) => {
    try {
      const { error } = await supabase.from("contas").insert(conta);
      if (error) throw error;
      toast.success("Conta criada com sucesso!");
      fetchContas();
      return { error: null };
    } catch (error) {
      console.error("Erro ao criar conta:", error);
      toast.error("Erro ao criar conta");
      return { error };
    }
  };

  const updateConta = async (id: string, conta: Partial<Conta>) => {
    try {
      const { error } = await supabase.from("contas").update(conta).eq("id", id);
      if (error) throw error;
      toast.success("Conta atualizada com sucesso!");
      fetchContas();
      return { error: null };
    } catch (error) {
      console.error("Erro ao atualizar conta:", error);
      toast.error("Erro ao atualizar conta");
      return { error };
    }
  };

  const deleteConta = async (id: string) => {
    try {
      // Verificar se existem transferências relacionadas a esta conta
      const { data: transferencias, error: transferError } = await supabase
        .from("transferencias")
        .select("id")
        .or(`conta_origem_id.eq.${id},conta_destino_id.eq.${id}`);

      if (transferError) {
        throw transferError;
      }

      if (transferencias && transferencias.length > 0) {
        toast.error(
          `Não é possível excluir a conta pois ela possui ${transferencias.length} transferência(s) associada(s). Exclua as transferências primeiro.`
        );
        return { error: new Error("Dependências encontradas") };
      }

      // Verificar se existem transações relacionadas a esta conta
      const { data: transacoes, error: transacaoError } = await supabase
        .from("transacoes")
        .select("id")
        .eq("conta_id", id);

      if (transacaoError) {
        throw transacaoError;
      }

      if (transacoes && transacoes.length > 0) {
        toast.error(
          `Não é possível excluir a conta pois ela possui ${transacoes.length} transação(ões) associada(s). Exclua as transações primeiro.`
        );
        return { error: new Error("Dependências encontradas") };
      }

      const { error } = await supabase.from("contas").delete().eq("id", id);
      if (error) throw error;
      toast.success("Conta excluída com sucesso!");
      fetchContas();
      return { error: null };
    } catch (error) {
      console.error("Erro ao excluir conta:", error);
      toast.error("Erro ao excluir conta");
      return { error };
    }
  };

  const saldoTotal = contas
    .filter((c) => c.ativo)
    .reduce((acc, conta) => acc + (conta.saldo_atual ?? 0), 0);

  return {
    contas,
    loading,
    saldoTotal,
    fetchContas,
    createConta,
    updateConta,
    deleteConta,
  };
}
